/**
 * The estate's locale set — JavaScript twin of `isg_common/i18n.py`.
 *
 * Kept in its own module (rather than inside index.js) so the constants can be
 * imported by code that must not pull in the whole engine — the formatters,
 * and any build-time script that checks catalogue coverage.
 *
 * Changing this list means changing the Python side too. They are two halves
 * of one decision.
 */

/** Ordered — this is the order a language picker renders in. */
export const LOCALES = [
  ['en-GB', 'English (UK)'],
  ['en-US', 'English (US)'],
  ['de', 'Deutsch'],
  ['cs', 'Čeština'],
];

export const SUPPORTED = LOCALES.map(([code]) => code);

/** The base catalogue every key is authored in, and the fallback for all others. */
export const DEFAULT_LOCALE = 'en-GB';

/**
 * Default currency per locale.
 *
 * A *display* default only — never infer the currency of a stored amount from
 * the reader's language. A price held in GBP is GBP whoever is looking at it;
 * this is for formatting figures that genuinely belong to the viewer's region.
 */
export const CURRENCIES = {
  'en-GB': 'GBP',
  'en-US': 'USD',
  de: 'EUR',
  cs: 'CZK',
};

/**
 * Coerce an arbitrary locale-ish string to a supported code, or null.
 *
 * Handles what browsers and Entra actually send: `de-DE`, `cs_CZ`, `EN-gb`.
 * Returns null when there is no reasonable match so callers can distinguish
 * "unsupported" from "the user chose English" — a silent default to en-GB
 * hides the difference, and that difference is what makes browser detection
 * safe to skip once a preference exists.
 */
export function normaliseLocale(value) {
  if (!value) return null;

  const tag = String(value).trim().replace(/_/g, '-');
  if (!tag) return null;

  const lowered = tag.toLowerCase();

  const exact = SUPPORTED.find((code) => code.toLowerCase() === lowered);
  if (exact) return exact;

  // Primary subtag: de-DE -> de, cs-CZ -> cs.
  const primary = lowered.split('-')[0];
  const byLanguage = SUPPORTED.find((code) => code.toLowerCase() === primary);
  if (byLanguage) return byLanguage;

  // English with an unrecognised region (en-AU, en-IE, bare 'en') resolves to
  // the base — a British catalogue is far closer for those readers than
  // falling through to "unsupported".
  if (primary === 'en') return DEFAULT_LOCALE;

  return null;
}
